import {
  LEDGER_SOURCE_LABEL,
  LEDGER_SOURCES,
  LEDGER_TYPES,
  LedgerSource,
  LedgerType,
  ledgerType,
} from '@bariendo/lib/revenue-ledger';
import { formatGivenFamilyName } from '@bariendo/lib/human-name';
import { Patient } from '@medplum/fhirtypes';
import dayjs from 'dayjs';
import { rowsToCsv } from '@/services/csv';
import { ListRangePresetId } from '@/components/Hub/listRange';

/** A row of the Revenue ledger as the bot returns it (BAR-3026). */
export interface RevenueLedgerEntry {
  id: string;
  kind: 'payment' | 'refund';
  occurredAt: string;
  /** Signed as cash: payments positive, refunds negative. */
  amount: number;
  /** Signed as cash; null when the processor hasn't reported it. */
  fee: number | null;
  net: number;
  source: string | null;
  transactionId: string | null;
  payerName: string | null;
  payerEmail: string | null;
  medplumPatientId: string | null;
  patientName: string | null;
  checkoutSessionId: string | null;
  productIds: string[];
  refundedChargeId: string | null;
  /** The table `id` is in, for writing the note or the patient back (BAR-3032). */
  record: 'charge' | 'refund';
  note: string | null;
  /** Where the patient comes from: the payment's order, a ledger link, or the refunded payment. */
  patientSource: 'order' | 'link' | 'payment' | null;
  orderStatus: string | null;
  /** A manual entry's method and bank account (BAR-3031). */
  method: string | null;
  bankAccount: string | null;
  /** For a refund, the type of the request it settles (BAR-3033). */
  refundRequestType: 'refund' | 'customer_service_fee' | null;
  /** When it is recognized as revenue; null is Deferred (BAR-3027). */
  recognizedAt: string | null;
  /** Why it has that date, as the payments backend computed it. */
  recognitionReason: string | null;
}

const RECOGNITION_REASON_LABEL: Record<string, string> = {
  needs_patient: 'Needs a patient',
  payment_date: 'Payment date',
  refund_issued: 'Refund issued',
  cancelled: 'Cancelled',
  cancelled_refund_not_issued: 'Cancelled, refund not issued',
  procedure_not_occurred: 'Procedure not occurred',
  procedure: 'Procedure',
  // A receivable's payment: the procedure or consultation it bills (BAR-3040).
  service_date: 'Service date',
};

/** The prototype's hover reason for a row's recognition date. */
export function recognitionReasonLabel(row: Pick<LedgerRow, 'recognitionReason' | 'type'>): string {
  if (row.recognitionReason === 'issued') return `${row.type} issued`;
  return row.recognitionReason ? (RECOGNITION_REASON_LABEL[row.recognitionReason] ?? '') : '';
}

/** `Check · Mercury`: the prototype's "how" for a manual entry, and the CSV's Method. */
export function ledgerMethod(row: Pick<RevenueLedgerEntry, 'method' | 'bankAccount'>): string {
  return [row.method, row.bankAccount].filter(Boolean).join(' · ');
}

export interface LedgerRow extends RevenueLedgerEntry {
  type: LedgerType;
  /** The patient's display name; null when the row has no patient. */
  patientDisplayName: string | null;
}

export const LEDGER_FILTER_ALL = 'all';

export interface LedgerFilters {
  type: LedgerType | typeof LEDGER_FILTER_ALL;
  source: LedgerSource | typeof LEDGER_FILTER_ALL;
}

export function buildLedgerRows(entries: RevenueLedgerEntry[], patientById: Map<string, Patient>): LedgerRow[] {
  return entries
    .map((entry) => {
      const patient = entry.medplumPatientId ? patientById.get(entry.medplumPatientId) : undefined;
      const medplumName = patient ? formatGivenFamilyName(patient.name?.[0]) : '';
      return {
        ...entry,
        type: ledgerType(entry, patient),
        patientDisplayName: entry.medplumPatientId ? medplumName || entry.patientName : null,
      };
    })
    .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime());
}

export function hasPatient(row: LedgerRow): boolean {
  return Boolean(row.medplumPatientId);
}

/** Type and Source. The No patient chip narrows these further, and counts from them. */
export function filterLedgerRows(rows: LedgerRow[], filters: LedgerFilters): LedgerRow[] {
  return rows.filter(
    (row) =>
      (filters.type === LEDGER_FILTER_ALL || row.type === filters.type) &&
      (filters.source === LEDGER_FILTER_ALL || row.source === filters.source)
  );
}

// Sums run in cents so a column of decimals totals to the cent.
const cents = (amount: number | null): number => Math.round((amount ?? 0) * 100);
const dollars = (value: number): number => value / 100;

export interface LedgerTypeSummary {
  type: LedgerType;
  count: number;
  amount: number;
  fee: number;
  net: number;
}

export interface LedgerSourceSummary {
  source: string;
  payments: number;
  refunds: number;
  customerServiceFees: number;
  fee: number;
  net: number;
}

/** The by-type table: only Types with rows, in the spec's order, and the total. */
export function summarizeByType(rows: LedgerRow[]): {
  rows: LedgerTypeSummary[];
  total: Omit<LedgerTypeSummary, 'type'>;
} {
  const byType = new Map<LedgerType, { count: number; amount: number; fee: number }>();
  for (const row of rows) {
    const sum = byType.get(row.type) ?? { count: 0, amount: 0, fee: 0 };
    byType.set(row.type, {
      count: sum.count + 1,
      amount: sum.amount + cents(row.amount),
      fee: sum.fee + cents(row.fee),
    });
  }

  const summaries = LEDGER_TYPES.filter((type) => byType.has(type)).map((type) => {
    const sum = byType.get(type) as { count: number; amount: number; fee: number };
    return {
      type,
      count: sum.count,
      amount: dollars(sum.amount),
      fee: dollars(sum.fee),
      net: dollars(sum.amount + sum.fee),
    };
  });
  const amount = rows.reduce((total, row) => total + cents(row.amount), 0);
  const fee = rows.reduce((total, row) => total + cents(row.fee), 0);
  return {
    rows: summaries,
    total: { count: rows.length, amount: dollars(amount), fee: dollars(fee), net: dollars(amount + fee) },
  };
}

/** The by-source table: only sources with rows, payments then payors, and the total. */
export function summarizeBySource(rows: LedgerRow[]): {
  rows: LedgerSourceSummary[];
  total: Omit<LedgerSourceSummary, 'source'>;
} {
  const empty = () => ({ payments: 0, refunds: 0, customerServiceFees: 0, fee: 0 });
  const bySource = new Map<string, ReturnType<typeof empty>>();
  const total = empty();

  for (const row of rows) {
    const source = row.source ?? '';
    const sum = bySource.get(source) ?? empty();
    const column =
      row.type === 'Refund' ? 'refunds' : row.type === 'Customer service fee' ? 'customerServiceFees' : 'payments';
    for (const target of [sum, total]) {
      target[column] += cents(row.amount);
      target.fee += cents(row.fee);
    }
    bySource.set(source, sum);
  }

  const known = LEDGER_SOURCES.filter((source) => bySource.has(source));
  const unknown = [...bySource.keys()].filter((source) => !(LEDGER_SOURCES as readonly string[]).includes(source));
  const toDollars = (sum: ReturnType<typeof empty>) => ({
    payments: dollars(sum.payments),
    refunds: dollars(sum.refunds),
    customerServiceFees: dollars(sum.customerServiceFees),
    fee: dollars(sum.fee),
    net: dollars(sum.payments + sum.refunds + sum.customerServiceFees + sum.fee),
  });

  return {
    rows: [...known, ...unknown].map((source) => ({
      source,
      ...toDollars(bySource.get(source) as ReturnType<typeof empty>),
    })),
    total: toDollars(total),
  };
}

/** A charge's processor is nullable in the payments backend; those rows still need a name (BAR-3125). */
export function sourceLabel(source: string | null): string {
  if (!source) {
    return 'Not recorded';
  }
  return LEDGER_SOURCE_LABEL[source as LedgerSource] ?? source.charAt(0) + source.slice(1).toLowerCase();
}

// U+2212, not a hyphen.
const MINUS = '−';

/** `$9,300.00`, `−$9,300.00`; with `sign`, `+$604.50`. */
/** `?noPatient=1` opens the Ledger on No patient over all time: the Imports history's Connect (BAR-3028). */
export const LEDGER_NO_PATIENT_PARAM = 'noPatient';

export function formatLedgerMoney(amount: number, { sign = false }: { sign?: boolean } = {}): string {
  const formatted = Math.abs(amount).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  if (amount < 0) {
    return `${MINUS}${formatted}`;
  }
  return sign && amount > 0 ? `+${formatted}` : formatted;
}

/** `3pm`, `3:42pm`; empty at exactly midnight, where an import or manual entry carries no time. */
export function formatLedgerTime(occurredAt: string): string {
  const at = dayjs(occurredAt);
  if (at.hour() === 0 && at.minute() === 0) {
    return '';
  }
  return at.format(at.minute() === 0 ? 'ha' : 'h:mma');
}

/** `Sep 21, 2026 3:42pm`. */
export function formatLedgerDate(occurredAt: string): string {
  return [dayjs(occurredAt).format('MMM D, YYYY'), formatLedgerTime(occurredAt)].filter(Boolean).join(' ');
}

export const LEDGER_CSV_HEADERS = [
  'Date',
  'Time',
  'Patient',
  'Payer',
  'Type',
  'Amount',
  'Merchant fee',
  'Net',
  'Recognition date',
  'Transaction ID',
  'Source',
  'Method',
  'Note',
];

/** Exactly the rows the list shows; a Deferred recognition date is blank. Method is a manual entry's only. */
export function ledgerCsv(rows: LedgerRow[]): string {
  return rowsToCsv(
    LEDGER_CSV_HEADERS,
    rows.map((row) => [
      dayjs(row.occurredAt).format('YYYY-MM-DD'),
      formatLedgerTime(row.occurredAt),
      row.patientDisplayName ?? '',
      row.payerName ?? '',
      row.type,
      row.amount.toFixed(2),
      row.fee === null ? '' : row.fee.toFixed(2),
      row.net.toFixed(2),
      row.recognizedAt ? dayjs(row.recognizedAt).format('YYYY-MM-DD') : '',
      row.transactionId ?? '',
      sourceLabel(row.source),
      ledgerMethod(row),
      row.note ?? '',
    ])
  );
}

export function ledgerCsvFilename(now: Date): string {
  return `revenue-${dayjs(now).format('YYYY-MM-DD')}.csv`;
}

/** The Transaction date options, labelled with the months and quarters they cover. */
export function ledgerDatePresets(now: Date): Array<{ id: ListRangePresetId; label: string }> {
  const today = dayjs(now);
  const quarter = (day: dayjs.Dayjs) => `Q${Math.floor(day.month() / 3) + 1} ${day.year()}`;
  return [
    { id: 'this-month', label: today.format('MMM YYYY') },
    { id: 'last-month', label: today.subtract(1, 'month').format('MMM YYYY') },
    { id: 'month-before-last', label: today.subtract(2, 'month').format('MMM YYYY') },
    { id: 'this-quarter', label: quarter(today) },
    { id: 'last-quarter', label: `Last quarter (${quarter(today.subtract(3, 'month'))})` },
    { id: 'all', label: 'All time' },
    { id: 'custom', label: 'Custom range' },
  ];
}

/** Recognized in's options: Any first, as the default, then the same periods (BAR-3027). */
export function ledgerRecognizedPresets(now: Date): Array<{ id: ListRangePresetId; label: string }> {
  const periods = ledgerDatePresets(now).filter((preset) => preset.id !== 'all');
  return [{ id: 'all', label: 'Any' }, ...periods];
}
